import { Autocomplete, FormControl, FormHelperText, FormLabel, TextField, Button, Box } from '@mui/material';
import { FC, FocusEventHandler, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { EquipmentTypeDto } from '../../dtos';
import { useGetEquipmentTypeQuery } from '../../store/generated/generatedApi';
import { emptyGuid, HourlyTypes } from '../../util';

export interface IEquipmentTypeSelectProps {
    selectedEquipmentTypeId: string;
    handleSelectedEquipmentTypeChange: (selectedEquipmentType?: EquipmentTypeDto) => void;
    required?: boolean;
    errorMessage?: string;
    onBlur?: FocusEventHandler<HTMLDivElement>;
    disabled?: boolean;
    hourlyType?: string;
    defaultToDTEquipmentType?: boolean; // If true, the default equipment type will be DT. Used for new material line items.
}

const primaryEquipmentMapping: Record<string, string[]> = {
    [HourlyTypes.Demurrage]: ['DT', 'SA', 'TA', 'QA', 'MI'],
    [HourlyTypes.ESH]: ['DT', 'SA', 'TA', 'MI'],
    [HourlyTypes.Hauling]: ['DT', 'CV', 'SA', 'TA', 'QA', 'MI'],
    [HourlyTypes.Overtime]: ['DT', 'SA', 'TA', 'QA', 'MI'],
    [HourlyTypes.PW]: ['DT'],
    [HourlyTypes.PW_Overtime]: ['DT'],
    [HourlyTypes.PW_Travel]: ['DT'],
    [HourlyTypes.Travel]: ['DT', 'SA', 'TA', 'MI'],
    [HourlyTypes.Equipment]: ['BC', 'BD', 'FL', 'LH', 'MI', 'RO']
};

export const EquipmentTypeSelect: FC<IEquipmentTypeSelectProps> = props => {
    const { selectedEquipmentTypeId, handleSelectedEquipmentTypeChange, required, errorMessage, onBlur, disabled, hourlyType, defaultToDTEquipmentType } = props;
    const { data: equipmentTypes, isLoading: isLoadingEquipmentTypes } = useGetEquipmentTypeQuery({
        sorting: ['TYPE+'],
        page: 0,
        pageSize: 10000,
        includeInactive: false
    });
    const [initialized, setInitialized] = useState(false);
    const [showAllTypes, setShowAllTypes] = useState(false);

    useEffect(() => {
        if (initialized || !equipmentTypes || equipmentTypes?.pageResults.length === 0) {
            return;
        }

        setInitialized(true);
    }, [equipmentTypes, initialized]);

    useEffect(() => {
        if (defaultToDTEquipmentType && !selectedEquipmentTypeId && (equipmentTypes?.pageResults?.length ?? 0) > 0) {
            const dt = equipmentTypes?.pageResults?.find(x => x.type === 'DT');
            if (dt) handleSelectedEquipmentTypeChange(dt);
        }
    }, [defaultToDTEquipmentType, equipmentTypes?.pageResults, selectedEquipmentTypeId, handleSelectedEquipmentTypeChange]);

    useEffect(() => {
        setShowAllTypes(false);
    }, [hourlyType]);

    useEffect(() => {
        if (!equipmentTypes || equipmentTypes.pageResults.length === 0 || !hourlyType || !initialized) {
            return;
        }

        const primaryTypes = primaryEquipmentMapping[hourlyType];
        if (primaryTypes && primaryTypes.length === 1) {
            const result = equipmentTypes.pageResults.find(x => x.type === primaryTypes[0]);
            if (result && result.id !== selectedEquipmentTypeId) {
                handleSelectedEquipmentTypeChange(result);
            }
        }
    }, [hourlyType, equipmentTypes, handleSelectedEquipmentTypeChange, initialized, selectedEquipmentTypeId]);

    const filteredEquipmentTypes = useMemo(() => {
        const allTypes = equipmentTypes?.pageResults ?? [];
        if (showAllTypes || !hourlyType || !primaryEquipmentMapping[hourlyType]) {
            return allTypes;
        }

        const primaryTypes = primaryEquipmentMapping[hourlyType];
        return allTypes.filter(et => primaryTypes.includes(et.type));
    }, [equipmentTypes?.pageResults, showAllTypes, hourlyType]);

    const handleSelectChange = useCallback((_: SyntheticEvent<Element, Event>, value: EquipmentTypeDto | { type: string, description: string, id: string } | null) => {
        if (value && value.type === 'MORE') {
            setShowAllTypes(true);
        } else if (value && value.type === 'LESS') {
            setShowAllTypes(false);
        } else {
            handleSelectedEquipmentTypeChange(value as EquipmentTypeDto ?? undefined);
        }
    }, [handleSelectedEquipmentTypeChange]);

    const currentEquipmentType = useMemo(() => {
        if (selectedEquipmentTypeId === '' || selectedEquipmentTypeId === emptyGuid) {
            return null;
        }
        return equipmentTypes?.pageResults.find(x => x.id === selectedEquipmentTypeId) ?? null;
    }, [equipmentTypes?.pageResults, selectedEquipmentTypeId]);

    const autocompleteOptions = useMemo(() => {
        const options: any[] = [...filteredEquipmentTypes];
        if (!showAllTypes && hourlyType && primaryEquipmentMapping[hourlyType] && equipmentTypes?.pageResults && equipmentTypes.pageResults.length > filteredEquipmentTypes.length) {
            options.push({ type: 'MORE', description: 'Show More...', id: 'MORE' });
        } else if (showAllTypes && hourlyType && primaryEquipmentMapping[hourlyType] && equipmentTypes?.pageResults && equipmentTypes.pageResults.length > primaryEquipmentMapping[hourlyType].length) {
            options.push({ type: 'LESS', description: 'Show Less...', id: 'LESS' });
        }
        return options;
    }, [filteredEquipmentTypes, showAllTypes, hourlyType, equipmentTypes?.pageResults]);

    return (
        <FormControl error={!!errorMessage} fullWidth required={required} disabled={disabled}>
            <FormLabel>Equipment Type</FormLabel>
            <Autocomplete
                value={currentEquipmentType}
                onChange={handleSelectChange}
                loading={isLoadingEquipmentTypes}
                options={autocompleteOptions}
                getOptionLabel={(option: any) => (option.type === 'MORE' || option.type === 'LESS') ? option.description : `${option.type} - ${option.description}`}
                renderInput={(params) => (
                    <TextField
                        error={!!errorMessage}
                        {...params}
                    />
                )}
                renderOption={(props, option) => {
                    if (option.type === 'MORE' || option.type === 'LESS') {
                        const { onClick, ...restProps } = props as any;
                        return (
                            <Box
                                component="li"
                                {...restProps}
                                key={option.id}
                                sx={{ justifyContent: 'center', p: 1 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setShowAllTypes(option.type === 'MORE');
                                }}
                            >
                                <Button size="small" variant="text" color="primary" sx={{ width: '100%', pointerEvents: 'none' }}>
                                    {option.description}
                                </Button>
                            </Box>
                        );
                    }
                    return (
                        <li {...props} key={option.id}>
                            {`${option.type} - ${option.description}`}
                        </li>
                    );
                }}
                onBlur={onBlur}
                disabled={disabled}
            />
            <FormHelperText>{errorMessage}</FormHelperText>
        </FormControl>
    )
}